import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('clinic_token');
    const storedUser = localStorage.getItem('user');
    if (token) {
      API.get('/auth/me')
        .then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('clinic_token');
          localStorage.removeItem('clinic_user');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else if (storedUser) {
      // Clean up stale user data if no token
      localStorage.removeItem('user');
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (emailOrPhone, mpin) => {
    const res = await API.post('/auth/login', { 
      email_or_phone: emailOrPhone, 
      mpin: mpin 
    });
    localStorage.setItem('clinic_token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await API.post('/auth/register', {
      email: data.email,
      mpin: data.mpin,
      name: data.name,
      clinic_name: data.clinic_name,
      specialty: data.specialty || 'General Practice',
      phone: data.phone,
      address: data.address,
      latitude: data.latitude,
      longitude: data.longitude,
      referral_code: data.referral_code
    });
    localStorage.setItem('clinic_token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  // For Google OAuth - set user directly after successful auth
  const setAuthUser = useCallback((token, userData) => {
    localStorage.setItem('clinic_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('clinic_token');
    localStorage.removeItem('clinic_user');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setAuthUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
